// CASL consent bases and expiry (spec 08 §8). Mirrored in SQL by public.record_consent (Task 21).
import type { ConsentBasis, Lang } from "./types.ts";

export const CONSENT_BASES: readonly ConsentBasis[] = Object.freeze([
  "express",
  "implied_conspicuous_publication",
  "implied_existing_relationship",
  "implied_inquiry",
  "none",
]);
export type ConsentRecord = { basis: ConsentBasis; expires_at: string | null };
export type ConsentStatus = "valid" | "expired" | "none";

/** Same semantics as Postgres `timestamptz + interval 'N months'`: the day is clamped to the target month's end. */
function addMonthsClamped(d: Date, months: number): Date {
  const total = d.getUTCMonth() + months;
  const year = d.getUTCFullYear() + Math.floor(total / 12);
  const month = ((total % 12) + 12) % 12;
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(
    Date.UTC(
      year,
      month,
      Math.min(d.getUTCDate(), daysInMonth),
      d.getUTCHours(),
      d.getUTCMinutes(),
      d.getUTCSeconds(),
      d.getUTCMilliseconds(),
    ),
  );
}

export function computeConsentExpiry(
  basis: ConsentBasis,
  obtainedAt: Date,
): Date | null {
  if (basis === "implied_existing_relationship")
    return addMonthsClamped(obtainedAt, 24);
  if (basis === "implied_inquiry") return addMonthsClamped(obtainedAt, 6);
  return null;
}

export function consentState(
  c: ConsentRecord | null | undefined,
  now: Date,
): {
  status: ConsentStatus;
  basis: ConsentBasis | null;
  expiresAt: string | null;
} {
  if (!c || c.basis === "none")
    return { status: "none", basis: c?.basis ?? null, expiresAt: null };
  // Fail closed: unparsable expiry, or an implied basis without any expiry (data bug), is expired.
  const impliedWithoutExpiry =
    c.expires_at === null &&
    (c.basis === "implied_existing_relationship" ||
      c.basis === "implied_inquiry");
  if (
    impliedWithoutExpiry ||
    (c.expires_at !== null &&
      !(new Date(c.expires_at).getTime() > now.getTime()))
  ) {
    return { status: "expired", basis: c.basis, expiresAt: c.expires_at };
  }
  return { status: "valid", basis: c.basis, expiresAt: c.expires_at };
}

export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
export type ConsentInput = {
  email: string;
  basis: string;
  evidence?: string | null;
  obtainedAt: string;
};
export type ConsentError =
  | "invalid_email"
  | "invalid_basis"
  | "evidence_required"
  | "invalid_date"
  | "future_date";

export function validateConsentInput(
  i: ConsentInput,
  now: Date,
):
  | {
      ok: true;
      value: {
        email: string;
        basis: ConsentBasis;
        evidence: string | null;
        obtainedAt: Date;
        expiresAt: Date | null;
      };
    }
  | { ok: false; error: ConsentError } {
  const email = (i.email ?? "").trim().toLowerCase();
  if (!EMAIL_RE.test(email)) return { ok: false, error: "invalid_email" };
  if (!(CONSENT_BASES as readonly string[]).includes(i.basis))
    return { ok: false, error: "invalid_basis" };
  const basis = i.basis as ConsentBasis;
  const evidence = (i.evidence ?? "").trim() || null;
  if (basis === "express" && !evidence)
    return { ok: false, error: "evidence_required" };
  const obtainedAt = new Date(i.obtainedAt);
  if (Number.isNaN(obtainedAt.getTime()))
    return { ok: false, error: "invalid_date" };
  if (obtainedAt.getTime() > now.getTime() + 86_400_000)
    return { ok: false, error: "future_date" };
  return {
    ok: true,
    value: {
      email,
      basis,
      evidence,
      obtainedAt,
      expiresAt: computeConsentExpiry(basis, obtainedAt),
    },
  };
}

export const CONSENT_REASON: Readonly<
  Record<Exclude<ConsentBasis, "none">, Record<Lang, string>>
> = Object.freeze({
  express: {
    fr: "vous avez accepté de recevoir nos courriels",
    en: "you agreed to receive our emails",
  },
  implied_conspicuous_publication: {
    fr: "votre adresse est publiée publiquement par votre entreprise et notre message concerne vos activités",
    en: "your address is publicly published by your business and our message relates to your activities",
  },
  implied_existing_relationship: {
    fr: "vous êtes client de Gavion",
    en: "you are a Gavion customer",
  },
  implied_inquiry: {
    fr: "vous avez communiqué avec Gavion récemment",
    en: "you recently contacted Gavion",
  },
});
const GENERIC_REASON: Record<Lang, string> = {
  fr: "votre entreprise est liée à nos services",
  en: "your business relates to our services",
};

export function consentReason(basis: ConsentBasis | null, lang: Lang): string {
  if (!basis || basis === "none") return GENERIC_REASON[lang];
  return CONSENT_REASON[basis][lang];
}
